/**
 * ProfilePage — user dashboard with tabbed profile controls.
 *
 * Tabs:
 *  - Users: profile info + recent activity scoped to current user.
 *  - Settings: AI-agent and light/dark preferences (frontend cookies).
 *  - Integrations: vendor key management entry points.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Bot,
  KeyRound,
  Moon,
  ShieldCheck,
  Sun,
  UserCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableHeadCell,
  TableRow,
  TableCell,
} from "@/components/ui/table-shell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/stores/auth";
import { useAuditLog } from "@/stores/audit";
import { useTheme } from "@/stores/theme";
import { getCookie, setCookie } from "@/lib/cookies";

const AI_ENABLED_COOKIE = "opus.ai.enabled";
const PROFILE_THEME_COOKIE = "opus.profile.theme";
const PROFILE_TAB_COOKIE = "opus.profile.tab";
const ACTIVITY_COLS = "0.8fr 0.7fr 0.7fr 2.2fr";
const PROFILE_TABS = ["users", "settings", "integrations"] as const;
type ProfileTab = (typeof PROFILE_TABS)[number];

export function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuth((s) => s.user);
  const setTheme = useTheme((s) => s.setTheme);

  // Derive activeTab from URL; fall back to cookie, then "users".
  // No state needed — the URL is the source of truth after the first render.
  const activeTab =
    toProfileTab(searchParams.get("tab")) ??
    toProfileTab(getCookie(PROFILE_TAB_COOKIE)) ??
    "users";

  const [aiEnabled, setAiEnabled] = useState<boolean>(() => {
    const aiCookie = getCookie(AI_ENABLED_COOKIE);
    return aiCookie === null ? true : aiCookie === "1";
  });

  // Capture mount-time URL state in refs so the initialization effect can
  // read them without listing reactive values that would cause re-runs.
  const initSearchParamsRef = useRef(searchParams);
  const initActiveTabRef = useRef(activeTab);

  useEffect(() => {
    const themeCookie = getCookie(PROFILE_THEME_COOKIE);
    if (themeCookie === "light" || themeCookie === "dark") {
      setTheme(themeCookie);
    }

    // Ensure the URL always reflects the active tab (handles bare /profile load).
    const params = new URLSearchParams(initSearchParamsRef.current);
    params.set("tab", initActiveTabRef.current);
    setSearchParams(params, { replace: true });
  }, [setTheme, setSearchParams]);

  function onChangeTab(value: string) {
    const next = toProfileTab(value);
    if (!next) return;

    setCookie(PROFILE_TAB_COOKIE, next);

    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  }

  function toggleAiEnabled() {
    const next = !aiEnabled;
    setAiEnabled(next);
    setCookie(AI_ENABLED_COOKIE, next ? "1" : "0");
  }

  function setThemeMode(mode: "light" | "dark") {
    setTheme(mode);
    setCookie(PROFILE_THEME_COOKIE, mode);
  }

  if (!user) {
    return (
      <div className="flex flex-col gap-6">
        <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
          Profile
        </h1>
        <Card>
          <CardContent>
            <CardTitle>Not signed in</CardTitle>
            <CardSub>
              Sign in to view your profile.{" "}
              <Link to="/login" className="text-violet underline">
                Go to login
              </Link>
              .
            </CardSub>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
          Profile
        </h1>
        <p className="text-sm text-fg-mute">
          Your account dashboard and preferences.
        </p>
      </header>

      <Tabs value={activeTab} onValueChange={onChangeTab}>
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>

        <UsersTabContent aiEnabled={aiEnabled} />

        <SettingsTabContent
          aiEnabled={aiEnabled}
          toggleAiEnabled={toggleAiEnabled}
          setThemeMode={setThemeMode}
        />

        <TabsContent value="integrations" className="mt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Card>
              <CardContent className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle>Meraki keys</CardTitle>
                  <CardSub>
                    {user.merakiKeyConfigured
                      ? "Configured. Manage your stored Meraki credentials."
                      : "Not configured. Set your key to unlock Meraki pages."}
                  </CardSub>
                </div>
                <Button asChild size="sm" variant="ghost">
                  <Link to="/meraki/keys">
                    <KeyRound size={14} />
                    Manage
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle>Aruba keys</CardTitle>
                  <CardSub>Manage your Aruba Central API credentials.</CardSub>
                </div>
                <Button asChild size="sm" variant="ghost">
                  <Link to="/aruba/keys">
                    <KeyRound size={14} />
                    Manage
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Users tab ---------- */

function UsersTabContent({ aiEnabled }: { aiEnabled: boolean }) {
  const user = useAuth((s) => s.user);
  const auditEvents = useAuditLog((s) => s.data);
  const loadAudit = useAuditLog((s) => s.load);

  useEffect(() => {
    void loadAudit();
  }, [loadAudit]);

  if (!user) return null;

  const role = user.roles?.[0] ?? "—";
  const initials =
    (user.fullName ?? "")
      .split(" ")
      .map((s) => s[0] ?? "")
      .slice(0, 2)
      .join("")
      .toUpperCase() ||
    user.username[0]?.toUpperCase() ||
    "?";

  const recentUserEvents = auditEvents
    .filter((e) => e.user?.toLowerCase() === user.username.toLowerCase())
    .slice(0, 8);

  const userActivityKpis = (() => {
    const total = recentUserEvents.length;
    let changes = 0;
    for (const e of recentUserEvents) {
      if (e.kind === "create" || e.kind === "update" || e.kind === "delete") {
        changes += 1;
      }
    }
    const lastAt = recentUserEvents[0]?.occurredAt ?? null;
    return { total, changes, lastAt };
  })();

  return (
    <TabsContent value="users" className="mt-4 flex flex-col gap-4">
      <Card>
        <CardContent className="flex items-center gap-5">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-aurora-soft text-xl font-bold text-violet">
            {initials}
          </div>
          <div className="flex-1">
            <div className="text-lg font-semibold text-fg">
              {user.fullName || user.username}
            </div>
            <div className="text-sm text-fg-dim">{user.email}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="neutral">
                <ShieldCheck size={12} />
                {role}
              </Badge>
              {user.isStaff ? (
                <Badge variant="neutral">
                  <UserCircle size={12} />
                  staff
                </Badge>
              ) : null}
              <Badge variant="neutral">
                <Bot size={12} />
                AI agent: {aiEnabled ? "on" : "off"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile
          label="Recent events"
          value={String(userActivityKpis.total)}
        />
        <KpiTile
          label="Config changes"
          value={String(userActivityKpis.changes)}
        />
        <KpiTile
          label="Last activity"
          value={
            userActivityKpis.lastAt ? timeAgo(userActivityKpis.lastAt) : "—"
          }
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="px-4 pt-4">
            <CardTitle>Recent activity</CardTitle>
            <CardSub>
              Your most recent actions in Opus.{" "}
              <Link to="/audit" className="text-violet underline">
                Open full audit log
              </Link>
            </CardSub>
          </div>
          <TableShell className="mt-3 rounded-none border-0 border-t border-line">
            <TableHeadRow columns={ACTIVITY_COLS}>
              <TableHeadCell>When</TableHeadCell>
              <TableHeadCell>Kind</TableHeadCell>
              <TableHeadCell>Vendor</TableHeadCell>
              <TableHeadCell>Summary</TableHeadCell>
            </TableHeadRow>
            {recentUserEvents.map((e) => (
              <TableRow key={e.id} columns={ACTIVITY_COLS}>
                <TableCell className="font-mono text-[11px] text-fg-dim">
                  {timeAgo(e.occurredAt)}
                </TableCell>
                <TableCell>
                  <Badge variant={kindBadge(e.kind)} size="sm">
                    {e.kind}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-[10.5px] uppercase tracking-wide text-fg-dim">
                  {e.vendor ?? "system"}
                </TableCell>
                <TableCell className="text-fg" title={e.summary}>
                  <span className="block max-w-[620px] truncate">
                    {e.summary}
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {recentUserEvents.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-fg-mute">
                No recent activity found for your user yet.
              </div>
            ) : null}
          </TableShell>
        </CardContent>
      </Card>
    </TabsContent>
  );
}

/* ---------- Settings tab ---------- */

function SettingsTabContent({
  aiEnabled,
  toggleAiEnabled,
  setThemeMode,
}: {
  aiEnabled: boolean;
  toggleAiEnabled: () => void;
  setThemeMode: (mode: "light" | "dark") => void;
}) {
  const theme = useTheme((s) => s.resolved);

  return (
    <TabsContent value="settings" className="mt-4 flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div>
            <CardTitle>AI agent</CardTitle>
            <CardSub>
              Toggle the assistant UI availability in your current browser.
            </CardSub>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-line bg-surface px-3 py-2.5">
            <span className="text-sm text-fg">Enable AI agent</span>
            <button
              type="button"
              onClick={toggleAiEnabled}
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                aiEnabled ? "bg-mint/20 text-mint" : "bg-line text-fg-dim"
              }`}
            >
              {aiEnabled ? "On" : "Off"}
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div>
            <CardTitle>Theme</CardTitle>
            <CardSub>
              Light and dark preference for this browser. Stored in a
              frontend cookie.
            </CardSub>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={theme === "light" ? "primary" : "ghost"}
              onClick={() => setThemeMode("light")}
            >
              <Sun size={14} />
              Light
            </Button>
            <Button
              type="button"
              variant={theme === "dark" ? "primary" : "ghost"}
              onClick={() => setThemeMode("dark")}
            >
              <Moon size={14} />
              Dark
            </Button>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
}

/* ---------- helpers ---------- */

function KpiTile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent>
        <div className="text-[11px] uppercase tracking-[0.08em] text-fg-mute">
          {label}
        </div>
        <div className="mt-1 text-xl font-extrabold text-aurora">{value}</div>
      </CardContent>
    </Card>
  );
}

function kindBadge(kind: string): "ok" | "info" | "warn" | "err" | "neutral" {
  switch (kind) {
    case "create":
      return "ok";
    case "update":
      return "info";
    case "delete":
      return "err";
    case "login":
      return "ok";
    case "logout":
      return "neutral";
    case "login_failed":
      return "warn";
    default:
      return "neutral";
  }
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function toProfileTab(value: string | null): ProfileTab | null {
  if (!value) return null;
  return PROFILE_TABS.includes(value as ProfileTab)
    ? (value as ProfileTab)
    : null;
}
